/**
 * HUD: nível, recursos (topo-esq), barra de XP (base). Modelo pull.
 * 10.6F — a barra de VIDA saiu do canto de cima: ela agora flutua SOBRE o Caçador
 * (PlayerHealthBar), do mesmo jeito que a dos monstros, para o jogador acompanhar sem
 * tirar o olho do boneco. O topo ficou só com nível e recursos.
 */
import * as THREE from "three";
import { i18n } from "../i18n/i18n";
import { fmtNum } from "../utils/format";
import type { Player } from "../player/Player";

const _v = new THREE.Vector3();
/** Altura (mundo) onde a barra do Caçador flutua.
 *  10.6H: subiu de 2,35 para 3,05 — em 2,35 ela encostava no capuz e virava parte do boneco. */
const PLAYER_BAR_Y = 3.05;
import type { XPSystem } from "../progression/XPSystem";

export class HUD {
  private readonly root: HTMLDivElement;
  private readonly lvBadge: HTMLElement;
  private readonly lvWord: HTMLElement;
  private readonly hpFill: HTMLElement;
  private readonly hpGhost: HTMLElement; // 10.9GT
  private lastHpFrac = 1;
  private readonly hpLabel: HTMLElement;
  /** 10.6F — barra de vida flutuante sobre o Caçador. */
  private readonly php: HTMLElement;
  private readonly xpFill: HTMLElement;
  private readonly xpWord: HTMLElement;
  private readonly essTotal: HTMLElement;
  private readonly coinTotal: HTMLElement;
  private readonly diamondTotal: HTMLElement;
  private buffsEl: HTMLElement | null = null;
  private buffsKey = "";

  constructor(uiRoot: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "hud";
    this.root.innerHTML = `
      <div class="hud-topleft">
        <div class="hud-level"><span class="lv-badge">LV 1</span><span class="lv-word"></span><button class="hud-set" aria-label="settings">⚙️</button></div>
        <!-- 10.9EB — a barra de XP mora AQUI, colada no nivel: e a mesma informacao (que nivel
             voce e / quanto falta pro proximo). Antes ficava sozinha no rodape, entre os dois
             botoes do polegar, com rotulo apagado e sem numero nenhum. -->
        <div class="hud-xp"><div class="bar xp"><div class="fill"></div></div><span class="xp-pct">0%</span></div>
        <div class="hud-res">
          <div class="hud-essence" title="Essence"><span class="ess-icon">◆</span><span class="ess-total">0</span></div>
          <div class="hud-coins" title="Fragmentos"><span class="coin-icon">🪙</span><span class="coin-total">0</span></div>
          <div class="hud-diamonds" title="Diamantes"><span class="dia-icon">💎</span><span class="dia-total">0</span></div>
        </div>
        <!-- 10.9EJ — BONUS ATIVOS. Contrato, boosts de XP/moeda/sorte, buff de magia, bencaos,
             Invasao e VIP ja existiam, mas NADA disso aparecia: o jogador comprava e nao via. -->
        <div class="hud-buffs"></div>
      </div>
      <div class="php"><div class="php-bar"><div class="php-ghost"></div><div class="php-fill"></div></div><div class="php-num"></div></div>`;
    uiRoot.appendChild(this.root);

    this.lvBadge = this.root.querySelector(".lv-badge")!;
    this.buffsEl = this.root.querySelector(".hud-buffs"); // 10.9EJ — guardado aqui: o Game move ele pra pilha
    // 10.9ER — tocar num bonus ativo mostra o que ele e (title nao existe no toque)
    this.buffsEl?.addEventListener("click", (e) => {
      const chip = (e.target as HTMLElement).closest<HTMLElement>(".hb-chip");
      if (!chip?.dataset.tip) return;
      this.showChipTip(chip, chip.dataset.tip);
    });
    this.lvWord = this.root.querySelector(".lv-word")!;
    this.hpFill = this.root.querySelector(".php-fill")!;
    this.hpGhost = this.root.querySelector(".php-ghost")!;
    this.hpLabel = this.root.querySelector(".php-num")!;
    this.php = this.root.querySelector(".php")!;
    this.xpFill = this.root.querySelector(".xp .fill")!;
    this.xpWord = this.root.querySelector(".xp-pct")!;
    this.essTotal = this.root.querySelector(".ess-total")!;
    this.coinTotal = this.root.querySelector(".coin-total")!;
    this.diamondTotal = this.root.querySelector(".dia-total")!;

    i18n.onChange(() => this.renderLabels());
    this.renderLabels();
  }

  private charName = ""; // 10.9AF — nome do personagem (mostrado no lugar de "Nível")
  /** Define o nome exibido no HUD (vazio = volta a mostrar "Nível"). */
  setCharName(name: string): void {
    this.charName = name.trim();
    this.renderLabels();
  }

  private renderLabels(): void {
    this.root.querySelector(".hud-set")?.setAttribute("aria-label", i18n.t("settings.title"));
    this.lvWord.textContent = this.charName || i18n.t("hud.level");
    /* 10.9EB — o rotulo virou a PORCENTAGEM (atualizada no update); nao ha mais texto fixo. */
  }

  update(player: Player, xp: XPSystem, essenceTotal: number, coins = 0, diamonds = 0, camera?: THREE.PerspectiveCamera): void {
    this.lvBadge.textContent = `LV ${xp.level}`;
    const hpFrac = Math.max(0, player.health.fraction);
    this.hpFill.style.transform = `scaleX(${hpFrac})`;
    // 10.9GT — fantasma: ao PERDER vida ele segue com atraso (mostra o tamanho do golpe); ao curar acompanha na hora
    this.hpGhost.style.transition = hpFrac >= this.lastHpFrac ? "none" : "";
    this.hpGhost.style.transform = `scaleX(${hpFrac})`;
    this.lastHpFrac = hpFrac;
    this.hpLabel.textContent = `${fmtNum(Math.ceil(player.health.current))}/${fmtNum(player.health.max)}`;
    this.hpFill.classList.toggle("low", hpFrac < 0.34);
    this.xpFill.style.transform = `scaleX(${Math.min(1, xp.fraction)})`;
    const pct = `${Math.floor(Math.min(1, xp.fraction) * 100)}%`; // 10.9EB — quanto falta, em numero
    if (this.xpWord.textContent !== pct) this.xpWord.textContent = pct;
    this.essTotal.textContent = String(essenceTotal);
    this.coinTotal.textContent = String(coins);
    this.diamondTotal.textContent = String(diamonds);
    // 10.6F — projeta a barra de vida acima da cabeça do Caçador
    if (camera) {
      _v.set(player.position.x, PLAYER_BAR_Y, player.position.z);
      _v.project(camera);
      if (_v.z > 1) {
        this.php.style.opacity = "0";
      } else {
        this.php.style.opacity = "1";
        const sx = (_v.x * 0.5 + 0.5) * window.innerWidth;
        const sy = (-_v.y * 0.5 + 0.5) * window.innerHeight;
        this.php.style.transform = `translate(-50%,-50%) translate(${sx}px, ${sy}px)`;
      }
    }
  }

  show(): void {
    this.root.hidden = false;
  }
  hide(): void {
    this.root.hidden = true;
  }

  /** 10.9EJ — desenha os bonus ativos (so re-renderiza quando algo muda). */
  setBuffs(list: { icon: string; text: string; tip: string; kind?: string }[]): void {
    const el = this.buffsEl;
    if (!el) return;
    const key = list.map((b) => b.icon + b.text).join("|");
    if (key === this.buffsKey) return;
    this.buffsKey = key;
    el.innerHTML = list
      .map((b) => `<span class="hb-chip${b.kind ? " hb-" + b.kind : ""}" data-tip="${b.tip.replace(/"/g, "&quot;")}"><span class="hb-ic">${b.icon}</span>${b.text ? `<span class="hb-t">${b.text}</span>` : ""}</span>`)
      .join("");
  }

  /** 10.9EJ — elemento da linha de bonus (o Game empilha junto com Missao/Contrato). */
  get buffsElement(): HTMLElement | null { return this.buffsEl; }

  /** 10.9ER — balaozinho com a explicacao do bonus, logo abaixo do chip, some em 2,6s. */
  private chipTip: HTMLDivElement | null = null;
  private chipTipTimer = 0;
  private showChipTip(chip: HTMLElement, text: string): void {
    if (!this.chipTip) {
      this.chipTip = document.createElement("div");
      this.chipTip.className = "hb-tip";
      document.body.appendChild(this.chipTip);
    }
    const r = chip.getBoundingClientRect();
    const t = this.chipTip;
    t.textContent = text;
    t.style.left = `${Math.max(8, Math.min(window.innerWidth - 228, r.left))}px`;
    t.style.top = `${r.bottom + 6}px`;
    t.hidden = false;
    window.clearTimeout(this.chipTipTimer);
    this.chipTipTimer = window.setTimeout(() => { t.hidden = true; }, 2600);
  }
}
